'use client';
import React from 'react';

import { cn } from '@skin-seoul/design-system';

import { useProductCard } from '../ProductCardContext';

import { useDebouncedWishlistWrite, useWishlistToggle, getWishlistItem } from '@skin-seoul/service';
import { LucideIcon } from '../../LucideIcon';

interface ProductCardWishListButtonProps {
  className?: string;
}

const HEART_ICON_SIZE = 20;

export const ProductCardWishListButton = ({ className, ...rest }: ProductCardWishListButtonProps) => {
  const { sku } = useProductCard();
  const { wishlistData, toggleWishlist, isOwnerResolved } = useWishlistToggle();
  const { isWished: isServerWished } = getWishlistItem(wishlistData?.skus ?? [], sku);

  const { isWished, toggle } = useDebouncedWishlistWrite({
    sku,
    isServerWished,
    isOwnerResolved,
    onToggle: toggleWishlist,
  });

  const handleWishlistToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
    // The card renders this as a sibling of its link, so no anchor should be in the propagation
    // path. Both calls are kept as a guard for any consumer that overlays it on one anyway:
    // `stopPropagation` alone would still let the anchor's default navigation run.
    e.stopPropagation();
    e.preventDefault();
    toggle();
  };

  return (
    <button
      className={cn(
        'cursor-pointer absolute top-1 right-1 desktop:top-2 desktop:right-2 z-[1] w-fit h-fit flex items-center justify-center bg-generic-0 rounded-[20px] border border-transparent p-2 transition-all duration-200 ease-in-out',
        'hover:bg-primary-50 hover:[&>svg]:stroke-primary-400',
        'active:[&>svg]:scale-[1.2] active:[&>svg]:fill-primary-400',
        className,
      )}
      onClick={handleWishlistToggle}
      aria-label={isWished ? 'Remove from wishlist' : 'Add to wishlist'}
      {...rest}
    >
      <LucideIcon
        name="Heart"
        size={HEART_ICON_SIZE}
        className={cn(
          'pointer-events-none',
          isWished ? 'stroke-primary-500 fill-primary-500' : 'stroke-neutral-400 fill-none',
        )}
      />
    </button>
  );
};

export default ProductCardWishListButton;
